import { sidebarCollapsedAtom } from '@/atoms/sidebar.atom';
import { iAction } from '@/layout/Skeleton';
import { cn } from '@/lib/utils';
import { ChevronRight } from '@mynaui/icons-react';
import { useAtomValue } from 'jotai';
import { FunctionComponent, MouseEvent, PropsWithChildren, useCallback, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useIsActive } from '.';
import PortalTooltip from '../PortalTooltip';
import Spinner from '../Spinner';

export const Group: FunctionComponent<
  PropsWithChildren & {
    label: string;
    icon?: React.ReactNode;
    href?: string;
    disabled?: boolean;
    badge?: string;
    isCollapsed?: boolean;
    padding?: boolean;
    action?: iAction;
    actionElement?: React.ReactNode;
    persistanceKey?: string;
    loading?: boolean;
    onPrefetch?: () => void;
    onClick?: () => void;
  }
> = ({
  children,
  label,
  icon,
  href,
  disabled,
  badge,
  padding = true,
  action,
  actionElement,
  persistanceKey,
  loading,
  onPrefetch,
  onClick,
}) => {
  const id = useId();
  const wrapper = useRef<HTMLDivElement>(null);
  const sideWrapper = useRef<HTMLDivElement>(null);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (persistanceKey) return localStorage.getItem(persistanceKey) !== 'true';
    return true;
  });
  const isSidebarCollapsed = useAtomValue(sidebarCollapsedAtom);
  const navigate = useNavigate();
  const isActive = useIsActive(href);
  const prefetchTimeout = useRef<ReturnType<typeof setTimeout>>();

  const _handleClick = useCallback(
    (e: MouseEvent) => {
      if (disabled) return;
      if (onClick) return onClick();
      else if (href) {
        e.preventDefault();

        if (e.metaKey || e.ctrlKey || href.startsWith('http')) window.open(href, '_blank');
        else navigate(href);
      } else {
        setIsCollapsed((prev) => !prev);
        if (persistanceKey) localStorage.setItem(persistanceKey, isCollapsed.toString());
      }
    },
    [href, navigate, persistanceKey],
  );

  const _handleMouseEnter = useCallback(() => {
    if (onPrefetch) {
      prefetchTimeout.current = setTimeout(onPrefetch, 50);
    }
    if (wrapper.current && sideWrapper.current) {
      const { top } = wrapper.current.getBoundingClientRect();

      sideWrapper.current.style.top = `${top}px`;
    }
  }, [onPrefetch]);

  return (
    <div
      className='group cursor-pointer select-none'
      onMouseEnter={_handleMouseEnter}
      onMouseLeave={() => clearTimeout(prefetchTimeout.current)}
      ref={wrapper}>
      <a
        data-tooltip-id={id}
        onClick={_handleClick}
        className={cn(
          'flex w-full font-medium items-center gap-2 border border-transparent overflow-hidden rounded-md p-2 text-left outline-none ring-sidebar-ring focus-visible:ring-2 text-slate-700/90 dark:text-stone-200 h-8 text-sm [&>svg]:shrink-0 [&>svg]:size-4 [&>svg]:stroke-2 user-select-none cursor-pointer',
          disabled && 'pointer-events-none opacity-50',
          isActive
            ? 'bg-white dark:bg-stone-900 border-slate-200 dark:border-stone-800 text-slate-900 shadow-sm/3'
            : 'active:bg-slate-200/50 active:text-slate-700 hover:bg-slate-200/25 dark:active:bg-stone-800/50 dark:active:text-stone-200 dark:hover:bg-stone-800/25',
          (action || actionElement) && 'group/item',
          isSidebarCollapsed && 'justify-center items-center p-0',
        )}>
        {loading ? <Spinner /> : icon}
        {!isSidebarCollapsed && <span className='flex-1'>{label}</span>}
        {badge && !isSidebarCollapsed && (
          <span className='text-[11px] font-medium bg-slate-500/70 text-white rounded px-1 py-[2px] leading-none'>
            {badge}
          </span>
        )}
        {!isSidebarCollapsed && (
          <div className='relative size-4 flex items-center justify-center '>
            {actionElement ? (
              <div
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                className='absolute invisible group-hover/item:visible opacity-0 group-hover/item:opacity-100 transition-all'>
                {actionElement}
              </div>
            ) : action?.icon ? (
              <>
                {action?.tooltip && (
                  <PortalTooltip id={`${id}-action`} delayShow={160}>
                    {action?.tooltip}
                  </PortalTooltip>
                )}
                <button
                  data-tooltip-id={`${id}-action`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    action.onClick?.();
                  }}
                  disabled={action.disabled}
                  className={cn(
                    'text-slate-500/70 dark:text-stone-100 p-1 rounded-sm hover:bg-slate-300/25 active:bg-slate-300/50 absolute [&>svg]:shrink-0 [&>svg]:size-4 [&>svg]:stroke-2 [&>svg]:text-slate-500 dark:[&>svg]:text-stone-100 invisible group-hover/item:visible opacity-0 group-hover/item:opacity-100 transition-all',
                  )}>
                  {action.icon}
                </button>
              </>
            ) : null}
            {children && (
              <ChevronRight
                onClick={(e) => {
                  e.preventDefault();
                  setIsCollapsed((prev) => !prev);
                }}
                className={cn(
                  'text-slate-500/70 dark:text-stone-100 size-4 transition-all absolute ',
                  !isCollapsed && 'rotate-90',
                  (action || actionElement) && 'group-hover/item:invisible group-hover/item:opacity-0',
                )}
              />
            )}
          </div>
        )}
      </a>
      {!isCollapsed && !isSidebarCollapsed && (
        <div className={cn(padding && 'ml-4 pl-2.5 border-l border-slate-200 dark:border-stone-800')}>{children}</div>
      )}
      {children && isSidebarCollapsed && (
        <div
          ref={sideWrapper}
          className='invisible overflow-hidden opacity-0 -translate-x-2 group-hover:translate-x-0 group-hover:opacity-100 group-hover:visible transition-[visible,transforms] fixed w-48 left-10 z-20 bg-white rounded-lg border border-slate-200/70 shadow-md/3 dark:bg-stone-900 dark:border-stone-800/70'>
          <div className='flex gap-1 text-slate-700 relative pl-4 pt-2 pb-1.5 p-1 items-center bg-slate-50 border-b border-slate-200/40 dark:bg-stone-900 dark:border-stone-800/40'>
            <span className='text-xs font-[550] line-clamp-1 overflow-ellipsis text-slate-700 dark:text-white'>
              {label}
            </span>
            {action?.icon && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  action.onClick?.();
                }}
                disabled={action.disabled}
                className={cn(
                  'text-slate-500/70 dark:text-stone-100 p-1 rounded-sm hover:bg-slate-300/25 active:bg-slate-300/50 absolute [&>svg]:shrink-0 [&>svg]:size-4 [&>svg]:stroke-2 transition-all right-1',
                )}>
                {action.icon}
              </button>
            )}
          </div>
          <div className='flex flex-col gap-1 p-1'>{children}</div>
        </div>
      )}
    </div>
  );
};
